import React, { useEffect, useMemo, useState } from 'react'
import {
  FaArrowUp,
  FaAward,
  FaBookOpen,
  FaCalendarAlt,
  FaChartLine,
  FaClock,
} from 'react-icons/fa'
import { BASE_URL_Upload } from '../Component/commonComponents/ApiEndpoints'
import { useSidebar } from '../Component/SidebarContext'

const REGISTRY_TITLE = 'Improvement Certificate'

const formatDdMmYyyy = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value.trim())) {
    return value.trim()
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const safeStr = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return fallback
  return s
}

const parsePercentage = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(String(v).replace(/%/g, '').trim())
  return Number.isFinite(n) ? n : null
}

const resolveMediaUrl = (src) => {
  const t = safeStr(src, '')
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('//')) return `https:${t}`
  const base = String(BASE_URL_Upload || '').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return base ? `${base}${path}` : t
}

const displayTitleFromProps = (certificateTitle) => {
  const t = safeStr(certificateTitle, '')
  if (!t || t === REGISTRY_TITLE) return 'Certificate of Improvement'
  return t
}

const normalizeImprovement = (rawCurrent, rawPrevious, rawImprovement) => {
  const current = parsePercentage(rawCurrent)
  const previous = parsePercentage(rawPrevious)
  const provided = parsePercentage(rawImprovement)

  let improvement = provided
  if (improvement === null && current !== null && previous !== null) {
    improvement = Number((current - previous).toFixed(2))
  }

  return { current, previous, improvement }
}

const getTag = (improvement) => {
  if (improvement === null) return ''
  if (improvement >= 15) return 'Most Improved Student'
  if (improvement >= 8) return 'Excellent Progress'
  if (improvement > 0) return 'Consistent Improvement'
  return 'Steady Effort'
}

const CertificateTemplate8 = ({
  studentName = 'Student Name',
  courseName = 'Course Name',
  instituteName = 'Institute Name',
  date = '',
  certificateId = '',
  certificateTitle = 'Certificate of Improvement',
  authorityName = 'Authorized Signatory',
  logoUrl = '',
  student: studentProp,
  course: courseProp,
  certificate: certificateProp,
  institute: instituteProp,
  selectedStudent,
  selectedCourse,
  previous_percentage,
  improvement_value,
  suppressSidebarOffset = false,
}) => {
  const certificate = certificateProp || {}
  const student = studentProp || selectedStudent || {}
  const course = courseProp || selectedCourse || {}
  const institute = instituteProp || {}

  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [photoFailed, setPhotoFailed] = useState(false)
  const [fallbackPhotoFailed, setFallbackPhotoFailed] = useState(false)

  useEffect(() => {
    if (suppressSidebarOffset) return undefined
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed, suppressSidebarOffset])

  useEffect(() => {
    setPhotoFailed(false)
    setFallbackPhotoFailed(false)
  }, [student?.student_image, student?.photo, student?.image])

  const data = useMemo(() => {
    const instName =
      safeStr(institute.institute_name) ||
      safeStr(institute.name) ||
      safeStr(instituteName)

    const studName = safeStr(student.name) || safeStr(studentName)
    const father = safeStr(student.father_name)
    const mother = safeStr(student.mother_name)
    const admission = safeStr(student.admission_number)

    const crsName =
      safeStr(course.course_name) || safeStr(course.name) || safeStr(courseName)
    const durationRaw = course.course_duration ?? course.duration
    const duration = (() => {
      if (durationRaw === null || durationRaw === undefined) return ''
      const s = String(durationRaw).trim()
      if (!s) return ''
      return /month/i.test(s) ? s : `${s} months`
    })()

    const start = formatDdMmYyyy(course.start_date ?? course.startDate)
    const end = formatDdMmYyyy(course.end_date ?? course.endDate)

    const certNum =
      safeStr(certificate.certificate_number) ||
      safeStr(certificate.certificate_id) ||
      safeStr(certificate.id != null ? String(certificate.id) : '') ||
      safeStr(certificateId)

    const issueRaw =
      certificate.issue_date ?? certificate.date ?? certificate.issueDate ?? date
    const issue = formatDdMmYyyy(issueRaw) || (typeof issueRaw === 'string' ? issueRaw : '')

    const grade = safeStr(certificate.grade)
    const { current, previous, improvement } = normalizeImprovement(
      certificate.percentage ?? certificate.current_percentage,
      previous_percentage ?? certificate.previous_percentage,
      improvement_value ?? certificate.improvement_value,
    )

    const imgRaw =
      student.student_image ?? student.photo ?? student.image ?? student.profile_image
    const photoSrc = resolveMediaUrl(imgRaw)

    const titlePrinted = displayTitleFromProps(certificateTitle)
    const tag = getTag(improvement)

    return {
      instName,
      studName,
      father,
      mother,
      admission,
      crsName,
      duration,
      start,
      end,
      certNum,
      issue,
      grade,
      current,
      previous,
      improvement,
      photoSrc,
      titlePrinted,
      tag,
    }
  }, [
    certificate,
    student,
    course,
    institute,
    studentName,
    courseName,
    instituteName,
    certificateId,
    certificateTitle,
    date,
    previous_percentage,
    improvement_value,
  ])

  const {
    instName,
    studName,
    father,
    mother,
    admission,
    crsName,
    duration,
    start,
    end,
    certNum,
    issue,
    grade,
    current,
    previous,
    improvement,
    photoSrc,
    titlePrinted,
    tag,
  } = data

  const period =
    start && end ? `${start} to ${end}` : start || end ? `${start || '—'} to ${end || '—'}` : ''

  const qrData = useMemo(() => {
    const parts = [instName, certNum || 'PREVIEW', issue || formatDdMmYyyy(new Date())].filter(
      Boolean,
    )
    return parts.join('|')
  }, [instName, certNum, issue])

  const qrSrc =
    typeof window !== 'undefined'
      ? `https://api.qrserver.com/v1/create-qr-code/?size=72x72&margin=0&data=${encodeURIComponent(
          qrData,
        )}`
      : ''

  const defaultPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const showPhoto = photoSrc && !photoFailed
  const logoResolved = resolveMediaUrl(logoUrl) || logoUrl
  const layoutMarginLeft = suppressSidebarOffset ? '0px' : marginLeft

  const handlePrint = () => {
    window.print()
  }

  return (
    <div
      className="certificate-template-8-page"
      style={{
        marginLeft: layoutMarginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

          .certificate-template-8-page {
            min-height: 100vh;
            background: linear-gradient(160deg, #e8f5ef 0%, #eef5ff 45%, #dbeafe 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            box-sizing: border-box;
          }

          .certificate-template-8-wrap {
            width: min(297mm, 100%);
          }

          .certificate-template-8-actions {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
          }

          .certificate-template-8-print-btn {
            border: none;
            background: linear-gradient(135deg, #0ea5a4, #2563eb);
            color: #fff;
            padding: 10px 18px;
            border-radius: 10px;
            font-family: 'Inter', sans-serif;
            font-weight: 700;
            font-size: 13px;
            cursor: pointer;
            box-shadow: 0 8px 16px rgba(37, 99, 235, 0.2);
          }

          .certificate-template-8-card {
            width: 100%;
            aspect-ratio: 297 / 210;
            max-height: min(90vh, 210mm);
            margin: 0 auto;
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 18px 42px rgba(15, 23, 42, 0.12);
            border: 1px solid #cbd5e1;
            overflow: hidden;
            position: relative;
          }

          .certificate-template-8-card::before {
            content: '';
            position: absolute;
            inset: 0;
            border: 4px solid transparent;
            border-image: linear-gradient(135deg, #22c55e, #0ea5e9, #2563eb) 1;
            pointer-events: none;
            z-index: 2;
          }

          .certificate-template-8-card::after {
            content: '';
            position: absolute;
            inset: 12px;
            border: 1px solid rgba(14, 165, 233, 0.24);
            pointer-events: none;
            z-index: 1;
          }

          .certificate-template-8-watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            z-index: 0;
          }

          .certificate-template-8-watermark-mark {
            font-family: 'Playfair Display', serif;
            font-size: clamp(52px, 12vw, 108px);
            color: rgba(14, 165, 233, 0.06);
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .certificate-template-8-arrow {
            position: absolute;
            width: 180px;
            height: 70px;
            border-left: 3px solid rgba(34, 197, 94, 0.16);
            border-bottom: 3px solid rgba(34, 197, 94, 0.16);
            transform: rotate(-24deg);
            border-bottom-left-radius: 100px;
            left: 22%;
            top: 57%;
          }

          .certificate-template-8-inner {
            position: relative;
            z-index: 3;
            height: 100%;
            box-sizing: border-box;
            padding: clamp(14px, 2vw, 22px) clamp(18px, 2.8vw, 30px) 16px;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }

          .certificate-template-8-head {
            text-align: center;
            position: relative;
            min-height: 72px;
            flex-shrink: 0;
          }

          .certificate-template-8-logo {
            width: 52px;
            height: 52px;
            margin: 0 auto 6px;
            border-radius: 50%;
            border: 2px solid #0ea5a4;
            object-fit: cover;
            background: #fff;
          }

          .certificate-template-8-logo-ph {
            width: 52px;
            height: 52px;
            margin: 0 auto 6px;
            border-radius: 50%;
            border: 2px dashed #93c5fd;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #93c5fd;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-8-photo {
            position: absolute;
            right: 0;
            top: 0;
            width: 78px;
            height: 96px;
            border: 2px solid #0ea5a4;
            border-radius: 8px;
            overflow: hidden;
            background: #fff;
          }

          .certificate-template-8-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-8-photo-ph {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            text-align: center;
            padding: 4px;
          }

          .certificate-template-8-institute {
            margin: 0;
            font-family: 'Inter', sans-serif;
            font-size: clamp(12px, 1.4vw, 14px);
            font-weight: 700;
            letter-spacing: 0.07em;
            text-transform: uppercase;
            color: #0284c7;
          }

          .certificate-template-8-title {
            margin: 8px 0 0;
            font-family: 'Playfair Display', serif;
            font-size: clamp(22px, 3vw, 32px);
            color: #111827;
            text-align: center;
            line-height: 1.2;
          }

          .certificate-template-8-title-accent {
            width: min(300px, 75%);
            height: 3px;
            margin: 8px auto 0;
            background: linear-gradient(90deg, transparent, #0ea5a4, transparent);
            border-radius: 99px;
          }

          .certificate-template-8-tag {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin-top: 8px;
            padding: 4px 12px;
            border-radius: 999px;
            background: linear-gradient(135deg, #22c55e, #0ea5a4);
            color: #fff;
            font-size: 10px;
            font-family: 'Inter', sans-serif;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }

          .certificate-template-8-main {
            margin-top: 10px;
            display: grid;
            grid-template-columns: 1fr min(120px, 22%);
            gap: 12px 16px;
            align-items: start;
            flex: 1;
            min-height: 0;
          }

          .certificate-template-8-body {
            font-family: 'Inter', sans-serif;
            font-size: clamp(11px, 1.15vw, 13px);
            line-height: 1.65;
            color: #4b5563;
            text-align: left;
          }

          .certificate-template-8-body p {
            margin: 0 0 10px;
          }

          .certificate-template-8-name {
            font-weight: 700;
            font-size: clamp(12px, 1.25vw, 14px);
            color: #111827;
          }

          .certificate-template-8-em {
            font-weight: 600;
            color: #1f2937;
          }

          .certificate-template-8-divider {
            height: 1px;
            background: linear-gradient(90deg, transparent, #bfdbfe, transparent);
            margin: 8px 0;
          }

          .certificate-template-8-improve {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
          }

          .certificate-template-8-box {
            padding: 8px;
            border: 1px solid #dbeafe;
            border-radius: 8px;
            background: linear-gradient(180deg, #f0f9ff 0%, #ecfeff 100%);
          }

          .certificate-template-8-label {
            font-size: 8px;
            color: #0284c7;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-weight: 700;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-8-value {
            margin-top: 2px;
            font-size: 10px;
            color: #111827;
            font-weight: 700;
            word-break: break-word;
          }

          .certificate-template-8-progress {
            margin-top: 6px;
            height: 8px;
            background: #e5e7eb;
            border-radius: 999px;
            overflow: hidden;
          }

          .certificate-template-8-progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #22c55e, #0ea5a4);
          }

          .certificate-template-8-details {
            margin-top: 8px;
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
          }

          .certificate-template-8-meta {
            margin-top: 8px;
            display: flex;
            flex-wrap: wrap;
            gap: 10px 16px;
            font-size: 10px;
            color: #4b5563;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-8-meta strong {
            color: #111827;
          }

          .certificate-template-8-side {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .certificate-template-8-qr img {
            width: 58px;
            height: 58px;
            display: block;
          }

          .certificate-template-8-qr-cap {
            font-size: 7px;
            color: #64748b;
            text-align: center;
            margin-top: 2px;
          }

          .certificate-template-8-footer {
            margin-top: auto;
            padding-top: 10px;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 10px;
            flex-shrink: 0;
          }

          .certificate-template-8-sign {
            text-align: center;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-8-seal {
            width: 42px;
            height: 42px;
            margin: 0 auto 4px;
            border: 2px solid #0ea5a4;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #0ea5a4;
            background: #f0fdfa;
          }

          .certificate-template-8-sign-line {
            border-top: 1px solid #374151;
            margin-top: 22px;
            padding-top: 5px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #111827;
          }

          .certificate-template-8-sign-sub {
            font-size: 8px;
            color: #6b7280;
            margin-top: 2px;
          }

          @media (max-width: 720px) {
            .certificate-template-8-main {
              grid-template-columns: 1fr;
            }
            .certificate-template-8-photo {
              position: relative;
              margin: 0 auto 8px;
            }
            .certificate-template-8-improve,
            .certificate-template-8-details {
              grid-template-columns: 1fr;
            }
            .certificate-template-8-footer {
              grid-template-columns: 1fr;
            }
          }

          @media print {
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            body * {
              visibility: hidden;
            }
            .certificate-template-8-page,
            .certificate-template-8-page * {
              visibility: visible;
            }
            .certificate-template-8-page {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: #fff;
              padding: 0;
              min-height: auto;
              margin-left: 0 !important;
            }
            .certificate-template-8-actions,
            .certificate-template-8-actions * {
              display: none !important;
              visibility: hidden !important;
            }
            .certificate-template-8-wrap {
              width: 100%;
            }
            .certificate-template-8-card {
              max-height: none;
              box-shadow: none;
              border-radius: 0;
              aspect-ratio: auto;
              min-height: 185mm;
            }
          }
        `}
      </style>

      <div className="certificate-template-8-wrap">
        <div className="certificate-template-8-actions">
          <button type="button" className="certificate-template-8-print-btn" onClick={handlePrint}>
            Print / Download
          </button>
        </div>

        <article className="certificate-template-8-card">
          <div className="certificate-template-8-watermark" aria-hidden>
            <div className="certificate-template-8-watermark-mark">Progress</div>
            <div className="certificate-template-8-arrow" />
          </div>

          <div className="certificate-template-8-inner">
            <header className="certificate-template-8-head">
              {logoResolved ? (
                <img src={logoResolved} alt="" className="certificate-template-8-logo" />
              ) : (
                <div className="certificate-template-8-logo-ph">Logo</div>
              )}
              <div className="certificate-template-8-photo" aria-label="Student photo">
                {showPhoto ? (
                  <img src={photoSrc} alt="" onError={() => setPhotoFailed(true)} />
                ) : !fallbackPhotoFailed ? (
                  <img
                    src={defaultPhoto}
                    alt=""
                    onError={() => setFallbackPhotoFailed(true)}
                  />
                ) : (
                  <div className="certificate-template-8-photo-ph">Photo</div>
                )}
              </div>

              <p className="certificate-template-8-institute">{instName || 'Institute Name'}</p>
              <h1 className="certificate-template-8-title">{titlePrinted}</h1>
              <div className="certificate-template-8-title-accent" />
              {tag ? (
                <span className="certificate-template-8-tag">
                  <FaAward aria-hidden /> {tag}
                </span>
              ) : null}
            </header>

            <main className="certificate-template-8-main">
              <section className="certificate-template-8-body">
                <p>
                  This is to proudly certify that{' '}
                  <span className="certificate-template-8-name">{studName || '—'}</span>
                  {father || mother ? (
                    <>
                      ,<br />
                      {father ? (
                        <>
                          S/o <span className="certificate-template-8-em">{father}</span>
                          {mother ? ' and ' : ''}
                        </>
                      ) : null}
                      {mother ? (
                        <>
                          M/o <span className="certificate-template-8-em">{mother}</span>
                        </>
                      ) : null}
                      ,
                    </>
                  ) : admission ? (
                    ','
                  ) : null}
                  {admission ? (
                    <>
                      <br />
                      bearing Admission No. <span className="certificate-template-8-em">{admission}</span>,
                    </>
                  ) : null}
                  <br />
                  has shown remarkable improvement in their academic performance.
                </p>

                <p>
                  During the course <span className="certificate-template-8-em">{crsName || '—'}</span>,
                  from <span className="certificate-template-8-em">{start || '—'}</span> to{' '}
                  <span className="certificate-template-8-em">{end || '—'}</span>, the student
                  demonstrated dedication, hard work, and consistent progress.
                </p>

                <div className="certificate-template-8-divider" />

                <div className="certificate-template-8-improve">
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">Previous Performance</div>
                    <div className="certificate-template-8-value">
                      {previous !== null ? `${previous}%` : '—'}
                    </div>
                  </div>
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">Current Performance</div>
                    <div className="certificate-template-8-value">
                      {current !== null ? `${current}%` : '—'}
                    </div>
                  </div>
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">Improvement</div>
                    <div className="certificate-template-8-value">
                      {improvement !== null ? `+${improvement}%` : '—'}
                    </div>
                  </div>
                </div>

                <div className="certificate-template-8-progress">
                  <div
                    className="certificate-template-8-progress-fill"
                    style={{
                      width:
                        improvement !== null
                          ? `${Math.max(0, Math.min(100, improvement * 5))}%`
                          : '0%',
                    }}
                  />
                </div>

                <div className="certificate-template-8-details">
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">
                      <FaBookOpen aria-hidden style={{ marginRight: 4 }} />
                      Course
                    </div>
                    <div className="certificate-template-8-value">{crsName || '—'}</div>
                  </div>
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">
                      <FaClock aria-hidden style={{ marginRight: 4 }} />
                      Duration
                    </div>
                    <div className="certificate-template-8-value">{duration || '—'}</div>
                  </div>
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">
                      <FaCalendarAlt aria-hidden style={{ marginRight: 4 }} />
                      Period
                    </div>
                    <div className="certificate-template-8-value">{period || '—'}</div>
                  </div>
                </div>

                <div className="certificate-template-8-details" style={{ marginTop: 8 }}>
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">
                      <FaChartLine aria-hidden style={{ marginRight: 4 }} />
                      Current Percentage
                    </div>
                    <div className="certificate-template-8-value">
                      {current !== null ? `${current}%` : '—'}
                    </div>
                  </div>
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">Grade</div>
                    <div className="certificate-template-8-value">{grade || '—'}</div>
                  </div>
                  <div className="certificate-template-8-box">
                    <div className="certificate-template-8-label">
                      <FaArrowUp aria-hidden style={{ marginRight: 4 }} />
                      Growth
                    </div>
                    <div className="certificate-template-8-value">{tag || 'Steady Effort'}</div>
                  </div>
                </div>

                <div className="certificate-template-8-meta">
                  <span>
                    <strong>Certificate No:</strong> {certNum || '—'}
                  </span>
                  <span>
                    <strong>Issue Date:</strong> {issue || '—'}
                  </span>
                </div>
              </section>

              <aside className="certificate-template-8-side">
                <div className="certificate-template-8-qr">
                  <img src={qrSrc} alt="" width={58} height={58} />
                  <div className="certificate-template-8-qr-cap">Verification</div>
                </div>
              </aside>
            </main>

            <footer className="certificate-template-8-footer">
              <div className="certificate-template-8-sign">
                <div className="certificate-template-8-sign-line">Class Teacher</div>
                <div className="certificate-template-8-sign-sub">Academic Mentor</div>
              </div>
              <div className="certificate-template-8-sign">
                <div className="certificate-template-8-sign-line">{authorityName}</div>
                <div className="certificate-template-8-sign-sub">Authorized Signatory</div>
              </div>
              <div className="certificate-template-8-sign">
                <div className="certificate-template-8-sign-line">Principal / Director</div>
                <div className="certificate-template-8-sign-sub">Institute Authority</div>
              </div>
              <div className="certificate-template-8-sign">
                <div className="certificate-template-8-seal">Seal</div>
                <div className="certificate-template-8-sign-line">Official Seal</div>
                <div className="certificate-template-8-sign-sub">Optional</div>
              </div>
            </footer>
          </div>
        </article>
      </div>
    </div>
  )
}

export default CertificateTemplate8
